import PropTypes from 'prop-types';
import { BsPeople } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import { formatDateWithTime } from '#lib/dateUtils';

const RecipientsCard = ({
	selectedLists,
	allContactLists,
	totalRecipients,
	isEditingLists,
	setIsEditingLists,
	onListToggle,
	onNavigate,
}) => (
	<Card>
		<div className="flex flex-row items-center justify-between space-y-0 pb-2">
			<div className="flex items-center gap-2">
				<BsPeople className="h-4 w-4 text-bgBase-800" />
				<h3 className="text-md font-semibold tracking-tight">Empfänger</h3>
			</div>
			<Button onClick={() => setIsEditingLists(!isEditingLists)} variant="outline" size="sm">
				{isEditingLists ? 'Fertig' : 'Bearbeiten'}
			</Button>
		</div>
		<div className="pt-0">
			{!isEditingLists ? (
				<RecipientsReadView
					selectedLists={selectedLists}
					allContactLists={allContactLists}
					totalRecipients={totalRecipients}
					onStartEditing={() => setIsEditingLists(true)}
				/>
			) : (
				<RecipientsEditView
					selectedLists={selectedLists}
					allContactLists={allContactLists}
					onListToggle={onListToggle}
					onNavigate={onNavigate}
				/>
			)}
		</div>
	</Card>
);

const RecipientsReadView = ({ selectedLists, allContactLists, totalRecipients, onStartEditing }) => {
	if (selectedLists.length === 0) {
		return (
			<div className="text-center py-8">
				<div className="w-12 h-12 bg-bgBase-400 rounded-full flex items-center justify-center mx-auto mb-3">
					<BsPeople className="w-6 h-6 text-bgBase-800" />
				</div>
				<p className="text-sm text-bgBase-800 mb-3">Keine Empfängerlisten ausgewählt</p>
				<Button onClick={onStartEditing} variant="outline" size="sm">
					Listen hinzufügen
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-2">
			{selectedLists.map((list) => {
				const fullListData = allContactLists.find((l) => l.id === list.contactLists.id);
				const recipientCountForList = fullListData?.emailRecipientCount ?? 0;

				return (
					<div
						key={list.contactLists.id}
						className="flex items-center justify-between p-2 bg-bgBase-300 rounded-card"
					>
						<span className="font-medium text-sm">{list.contactLists.name}</span>
						<div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-secondary text-secondary-foreground">
							{recipientCountForList} Empfänger
						</div>
					</div>
				);
			})}
			<p className="text-sm text-bgBase-800 pt-2">Gesamt: {totalRecipients} Empfänger</p>
		</div>
	);
};

const RecipientsEditView = ({ selectedLists, allContactLists, onListToggle, onNavigate }) => {
	if (!allContactLists || allContactLists.length === 0) {
		return (
			<div className="space-y-3">
				<div className="text-center py-8">
					<div className="w-12 h-12 bg-bgBase-400 rounded-full flex items-center justify-center mx-auto mb-3">
						<BsPeople className="w-6 h-6 text-bgBase-800" />
					</div>
					<p className="text-sm text-bgBase-800 mb-3">Keine Kontaktlisten verfügbar</p>
					<Button onClick={() => onNavigate('/contact-lists')} variant="outline" size="sm">
						Listen erstellen
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="max-h-80 overflow-y-auto space-y-2">
				{allContactLists.map((list) => {
					const isSelected =
						selectedLists?.some((selectedList) => {
							const selectedId = selectedList.contactLists
								? selectedList.contactLists.id
								: selectedList.id;
							return selectedId === list.id;
						}) || false;

					return (
						<div
							key={list.id}
							className="flex items-start space-x-3 p-3 border rounded-card hover:bg-bgBase-300 transition-colors cursor-pointer"
							onClick={() => onListToggle(list.id)}
						>
							<div className="mt-0.5">
								<Checkbox
									id={`list-${list.id}`}
									checked={isSelected}
									onChange={() => onListToggle(list.id)}
								/>
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2">
									<h4 className="font-medium text-sm">{list.name}</h4>
									<div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-secondary text-secondary-foreground">
										<BsPeople className="h-3 w-3 mr-1" />
										{list.emailRecipientCount ?? 0}
									</div>
								</div>
								<p className="text-xs text-bgBase-800 mt-1">
									Erstellt am {formatDateWithTime(list.createdAt)}
								</p>
							</div>
						</div>
					);
				})}
			</div>
			<div className="pt-3 border-t">
				<p className="text-xs text-text">
					{selectedLists.length} {selectedLists.length === 1 ? 'Liste' : 'Listen'} ausgewählt
				</p>
			</div>
		</div>
	);
};

RecipientsCard.propTypes = {
	selectedLists: PropTypes.array.isRequired,
	allContactLists: PropTypes.array.isRequired,
	totalRecipients: PropTypes.number.isRequired,
	isEditingLists: PropTypes.bool.isRequired,
	setIsEditingLists: PropTypes.func.isRequired,
	onListToggle: PropTypes.func.isRequired,
	onNavigate: PropTypes.func.isRequired,
};

RecipientsReadView.propTypes = {
	selectedLists: PropTypes.array.isRequired,
	allContactLists: PropTypes.array.isRequired,
	totalRecipients: PropTypes.number.isRequired,
	onStartEditing: PropTypes.func.isRequired,
};

RecipientsEditView.propTypes = {
	selectedLists: PropTypes.array.isRequired,
	allContactLists: PropTypes.array.isRequired,
	onListToggle: PropTypes.func.isRequired,
	onNavigate: PropTypes.func.isRequired,
};

export default RecipientsCard;
